import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GitMerge, Plus, X, Archive, CheckCircle2 } from 'lucide-react';
import { corpActionApi, type CorporateAction } from '@/shared/adminApi';

const ACTION_TYPES = ['split', 'consolidation', 'bonus', 'demerger', 'merger', 'name_change', 'fv_change'] as const;
type ActionType = typeof ACTION_TYPES[number];

export function CorporateActionsPage() {
  const qc = useQueryClient();
  const [includeRetired, setIncludeRetired] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['corp-actions', includeRetired],
    queryFn: () => corpActionApi.list(includeRetired),
  });

  const retire = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      corpActionApi.retire(id, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['corp-actions'] }),
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-axis-ink inline-flex items-center gap-2">
            <GitMerge className="w-6 h-6 text-axis-maroon" />
            Corporate Action Master
          </h1>
          <p className="text-sm text-axis-ash mt-1">
            Curated ISIN mappings for splits, bonuses, demergers, mergers, and FV changes.
            The recon pipeline builds its resolution graph from active rows on every job.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowAdd(true)}>
          <Plus className="w-4 h-4" /> Add corporate action
        </button>
      </div>

      <div className="mb-4">
        <label className="text-sm text-axis-ash inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeRetired}
            onChange={(e) => setIncludeRetired(e.target.checked)}
          />
          Show retired entries
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-axis-cloud-100 text-axis-ash text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">From ISIN</th>
              <th className="text-left px-4 py-3">To ISIN</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Ratio</th>
              <th className="text-left px-4 py-3">Effective</th>
              <th className="text-left px-4 py-3">Notes</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-axis-cloud-200">
            {isLoading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-axis-ash">Loading…</td></tr>
            ) : !data?.rows?.length ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-axis-ash">No corporate actions on file.</td></tr>
            ) : data.rows.map((ca) => (
              <tr key={ca.id} className={`hover:bg-axis-cloud-50 ${ca.retired_at ? 'opacity-60' : ''}`}>
                <td className="px-4 py-3 font-mono text-xs">{ca.from_isin}</td>
                <td className="px-4 py-3 font-mono text-xs">{ca.to_isin}</td>
                <td className="px-4 py-3">
                  <span className="pill bg-axis-cloud-100 text-axis-ash">{ca.action_type}</span>
                </td>
                <td className="px-4 py-3 text-right text-xs">{ca.ratio_from} : {ca.ratio_to}</td>
                <td className="px-4 py-3 text-xs">{ca.effective_date}</td>
                <td className="px-4 py-3 text-xs text-axis-ash max-w-md truncate">{ca.notes ?? '—'}</td>
                <td className="px-4 py-3">
                  {ca.retired_at ? (
                    <span className="pill bg-axis-cloud-100 text-axis-ash">retired</span>
                  ) : (
                    <span className="pill bg-emerald-100 text-emerald-300 border border-emerald-400/30">active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {!ca.retired_at && (
                    <button
                      type="button"
                      className="text-xs text-axis-ash hover:text-axis-maroon inline-flex items-center gap-1"
                      onClick={() => {
                        if (confirm(`Retire corporate action ${ca.from_isin} → ${ca.to_isin}? Existing recon jobs will not be re-computed.`)) {
                          retire.mutate({ id: ca.id });
                        }
                      }}
                    >
                      <Archive className="w-3.5 h-3.5" /> Retire
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddDialog onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    from_isin: '', to_isin: '',
    action_type: 'fv_change' as ActionType,
    ratio_from: 1, ratio_to: 1,
    effective_date: '',
    announcement_source: '', notes: '',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => corpActionApi.create({
      from_isin: form.from_isin.toUpperCase().trim(),
      to_isin: form.to_isin.toUpperCase().trim(),
      action_type: form.action_type,
      ratio_from: form.ratio_from,
      ratio_to: form.ratio_to,
      effective_date: form.effective_date,
      announcement_source: form.announcement_source || null,
      notes: form.notes || null,
    } as Partial<CorporateAction> as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['corp-actions'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const ready = form.from_isin && form.to_isin && form.effective_date &&
                form.ratio_from > 0 && form.ratio_to > 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="glass-raised rounded-2xl max-w-2xl w-full">
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink-50 tracking-tight">Add corporate action</h2>
          <button type="button" onClick={onClose} className="text-ink-400 hover:text-ink-50 transition"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="From ISIN" required>
              <input
                className="input"
                placeholder="INE748C01020"
                value={form.from_isin}
                onChange={(e) => setForm({ ...form, from_isin: e.target.value })}
              />
            </Field>
            <Field label="To ISIN" required>
              <input
                className="input"
                placeholder="INE748C01038"
                value={form.to_isin}
                onChange={(e) => setForm({ ...form, to_isin: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Action type" required>
            <select
              className="input"
              value={form.action_type}
              onChange={(e) => setForm({ ...form, action_type: e.target.value as ActionType })}
            >
              {ACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Ratio from" required>
              <input type="number" min={0.000001} step={0.000001} className="input"
                     value={form.ratio_from}
                     onChange={(e) => setForm({ ...form, ratio_from: Number(e.target.value) })}/>
            </Field>
            <Field label="Ratio to" required>
              <input type="number" min={0.000001} step={0.000001} className="input"
                     value={form.ratio_to}
                     onChange={(e) => setForm({ ...form, ratio_to: Number(e.target.value) })}/>
            </Field>
          </div>
          <Field label="Effective date" required>
            <input type="date" className="input"
                   value={form.effective_date}
                   onChange={(e) => setForm({ ...form, effective_date: e.target.value })}/>
          </Field>
          <Field label="Announcement source">
            <input type="text" placeholder="BSE / NSE / company announcement URL" className="input"
                   value={form.announcement_source}
                   onChange={(e) => setForm({ ...form, announcement_source: e.target.value })}/>
          </Field>
          <Field label="Notes">
            <textarea rows={3} className="input"
                      placeholder="Context, ratio interpretation, intermediate ISIN if any…"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}/>
          </Field>
          {error && (
            <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded p-2">{error}</div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-axis-cloud-200 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary"
                  disabled={!ready || create.isPending}
                  onClick={() => create.mutate()}>
            <CheckCircle2 className="w-4 h-4" />
            {create.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <style>{`.input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #E5D7DE; border-radius: 0.375rem; font-size: 0.875rem; }
      .input:focus { outline: none; box-shadow: 0 0 0 2px #97144D; }`}</style>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-axis-ash mb-1">
        {label}{required && <span className="text-axis-maroon">*</span>}
      </div>
      {children}
    </label>
  );
}
